import {
  SHIPMENTS_PAGINATED_FETCH_STARTED,
  SHIPMENTS_PAGINATED_FETCH_SUCCEEDED,
  SHIPMENTS_PAGINATED_FETCH_FAILED
} from '#action/types';
import { webPortalApi } from '#config/web-portal-api';
import { snackBarError } from '#action/fetch';
import { shipmentEventType } from '#type';
import { getRequestOptions } from '#helper';

let abortController = new AbortController();

export const fetchFilterData = () => async (dispatch, getState) => {
  abortController.abort();
  try {
    dispatch({
      type: SHIPMENTS_PAGINATED_FETCH_STARTED
    });

    const { order, orderBy } = getState().shipmentsPaginated;
    const { shipment } = getState().listFilter;
    const searchParams = new URLSearchParams({
      order,
      orderBy,
      offset: 0,
      limit: 50,
      pagination: 1
    });

    const searchValue = shipment?.search?.value;
    const projectValue = shipment?.project?.value;
    const statusValue = shipment?.shipmentStatus?.value;

    if (searchValue) {
      searchParams.set('filter', searchValue);
      searchParams.set('filterBy', ['label', 'uid', 'project']);
    }

    if (projectValue) {
      const ids = projectValue.map(v => v.data.id).join(',');

      if (ids) {
        searchParams.set('project', ids);
      }
    }

    if (statusValue?.length) {
      const statusCodes = statusValue.map(v => v.data.value).join(',');
      searchParams.set('status', statusCodes);
    }

    abortController = new AbortController();
    const { signal } = abortController;

    // Debounce to prevent sending multiple requests in a short time
    await new Promise(res => {
      setTimeout(() => {
        res();
      }, 200);
    });

    const projectPaginationURL = webPortalApi.buildApiUrl(`shipment/details?${searchParams.toString()}`);
    const res = await fetch(projectPaginationURL, { ...getRequestOptions(), signal });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not JSON');
    }

    if (!res.ok) {
      dispatch(snackBarError({ message: 'Error in request' }));
    }

    let shipments = [];
    let last;
    const response = await res.json();

    if (Array.isArray(response)) {
      shipments = response ?? [];
    } else {
      ({ shipments = [], last } = response);
    }

    if (!shipments.length) {
      dispatch({
        type: SHIPMENTS_PAGINATED_FETCH_SUCCEEDED,
        payload: {
          shipments
        },
        totalCount: last
      });

      return;
    }

    if (shipments.length > 0) {
      const shipmentIds = shipments.map(s => s.id).join(',');
      let deviceIds = shipments.reduce((a, c) => {
        c.devices?.forEach(d => a.push(d.id));
        return a;
      }, []);
      deviceIds = [...new Set(deviceIds)];

      const logsURL = webPortalApi.buildApiUrl('shipment/log');
      const logsParams = new URLSearchParams({
        shipment: shipmentIds, event: shipmentEventType.TRIGGER_ALERT
      });

      const devicesURL = webPortalApi.buildApiUrl('device/distinct');
      const devicesParams = new URLSearchParams({ ids: deviceIds });

      const [logsResponse, deviceResponse] = await Promise.all([
        fetch(`${logsURL}?${shipmentIds.length > 0 && logsParams.toString()}`, getRequestOptions()),
        fetch(`${devicesURL}?${devicesURL.length > 0 && devicesParams.toString()}`, getRequestOptions())
      ]).then(async reps => Promise.all(reps.map(r => r.json())));

      dispatch({
        type: SHIPMENTS_PAGINATED_FETCH_SUCCEEDED,
        payload: {
          shipments,
          logs: logsResponse?.logs,
          devices: deviceResponse
        },
        totalCount: last
      });

      return;
    }

    dispatch({
      type: SHIPMENTS_PAGINATED_FETCH_SUCCEEDED,
      payload: {
        shipments,
        logs: []
      },
      totalCount: last
    });
  } catch (e) {
    if (e.name === 'AbortError') {
      return;
    }
    dispatch({
      type: SHIPMENTS_PAGINATED_FETCH_FAILED,
      error: e
    });
  }
};
